"use client";

import { UpcomingPortfolioSummary } from "@/components/portfolio/UpcomingPortfolioSummary";
import { Button } from "@/components/ui/Button";
import {
  ArrowRight,
  Bell,
  Building2,
  CalendarClock,
  LineChart,
  ReceiptText,
} from "lucide-react";
import Link from "next/link";
import { useLanguage } from "@/context/LanguageContext";
import {LazyPortfolioPreview} from "@/components/index/sections/hero/LazyPortfolioPreview";

export function CombinedPortfolioSection() {
  const { language } = useLanguage();
  const copy =
    language === "cs"
      ? {
          eyebrow: "Správa portfolia pro majitele nemovitostí",
          title: "Správa realitního portfolia.",
          titleAccent: "Bez tabulek a složek.",
          description:
            "Mějte hodnotu, finance, dokumenty, provoz a důležité termíny každé nemovitosti v jednom přehledu. SOFTIQ zjednodušuje každodenní správu portfolia bytů, domů i komerčních nemovitostí.",
          portfolioAction: "Prohlédnout portfolio",
          watchdogAction: "Najít další nemovitost",
          features: [
            {
              icon: Building2,
              title: "Přehled realitního portfolia",
              text: "Hodnota, stav i využití každého bytu, domu nebo komerční nemovitosti.",
            },
            {
              icon: ReceiptText,
              title: "Finance a cash flow",
              text: "Příjmy, výdaje, financování a další platby v jednom přehledu.",
            },
            {
              icon: LineChart,
              title: "Výkonnost portfolia",
              text: "Sledujte tržní cenu, vývoj hodnoty a každodenní provoz nemovitostí.",
            },
            {
              icon: CalendarClock,
              title: "Dokumenty a důležité termíny",
              text: "Smlouvy, revize, výročí a upozornění bez hledání v poznámkách.",
            },
          ],
        }
      : {
          eyebrow: "SOFTIQ · My portfolio",
          title: "All your properties.",
          titleAccent: "Know how each one performs.",
          description:
            "Keep each property's value, finances, documents, operations, and important dates together in one place.",
          portfolioAction: "Explore the portfolio",
          watchdogAction: "Find another property",
          features: [
            {
              icon: Building2,
              title: "Properties in one place",
              text: "Value, condition, and use for every apartment, house, or rental.",
            },
            {
              icon: ReceiptText,
              title: "Finances and cash flow",
              text: "Income, expenses, financing, and other payments in one overview.",
            },
            {
              icon: LineChart,
              title: "Value and operations",
              text: "Track market price, value development, and day-to-day operations.",
            },
            {
              icon: CalendarClock,
              title: "Documents and important dates",
              text: "Contracts, inspections, anniversaries, and reminders without searching through notes.",
            },
          ],
        };

  return (
    <section
      id="produkty"
      className="section-light bg-white px-5 py-20 text-softiq-copy dark:bg-softiq-dark-page dark:text-white sm:px-8 lg:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-14 lg:grid-cols-[0.84fr_1.16fr] lg:items-center">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-softiq-primary/60 dark:text-softiq-secondary">
              {copy.eyebrow}
            </p>
            <h2 className="mt-5 max-w-xl text-[clamp(2.8rem,5vw,5.2rem)] font-light leading-[0.93] tracking-[-0.05em] text-softiq-copy dark:text-white">
              {copy.title}
              <span className="mt-2 block font-sans font-medium not-italic text-softiq-secondary-shade-700 dark:text-softiq-secondary">
                {copy.titleAccent}
              </span>
            </h2>
            <p className="mt-7 max-w-lg text-base leading-7 text-softiq-copy-muted dark:text-white/65">
              {copy.description}
            </p>

            <div className="mt-9 border-t border-softiq-border-subtle dark:border-white/15">
              {copy.features.map((feature) => (
                <div
                  key={feature.title}
                  className="grid grid-cols-[36px_1fr] gap-4 border-b border-softiq-border-subtle py-5 dark:border-white/15"
                >
                  <feature.icon
                    className="mt-0.5 size-4 text-softiq-primary/65 dark:text-softiq-secondary"
                    aria-hidden
                  />
                  <div>
                    <h3 className="text-sm font-medium text-softiq-copy dark:text-white">
                      {feature.title}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-softiq-copy-muted dark:text-white/50">
                      {feature.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                variant="default"
                size="lg"
                className="h-12 rounded-full px-6"
              >
                <Link href="/portfolio">
                  {copy.portfolioAction} <ArrowRight aria-hidden />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 rounded-full border-softiq-border bg-transparent px-6 text-softiq-primary hover:bg-softiq-secondary-shade-100/70 dark:border-white/25 dark:bg-transparent dark:text-white dark:hover:bg-white/10"
              >
                <Link href="/hlidaci-pes">
                  <Bell className="size-4" aria-hidden /> {copy.watchdogAction}
                </Link>
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <LazyPortfolioPreview lightSurface />
            <UpcomingPortfolioSummary compact language={language} />
          </div>
        </div>
      </div>
    </section>
  );
}
